import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Cookies from "js-cookie";
import { useSelector, useDispatch } from "react-redux";
import "./SettingsPrivacy.css";

const XIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="18" y1="6" x2="6" y2="18"/>
    <line x1="6" y1="6" x2="18" y2="18"/>
  </svg>
);

const ChevronRightIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="9 18 15 12 9 6"/>
  </svg>
);

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8000";

const monthNames = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];

const PhoneIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
  </svg>
);

const ChevronLeftIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="15 18 9 12 15 6"/>
  </svg>
);

export default function PersonalDetails({ setVisible }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showContactModal, setShowContactModal] = useState(false);
  const [showEmailAccountModal, setShowEmailAccountModal] = useState(false);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [showDeleteAccountModal, setShowDeleteAccountModal] = useState(false);
  const [showDeleteEmailModal, setShowDeleteEmailModal] = useState(false);
  const [isDeletingEmail, setIsDeletingEmail] = useState(false);
  const [isDeletingAccount, setIsDeletingAccount] = useState(false);

  useEffect(() => {
    const fetchUserProfile = async () => {
      if (user && user.token) {
        try {
          // Try to get user profile from API
          const res = await axios.get(`${API_URL}/getProfile/${user.username}`, {
            headers: { Authorization: `Bearer ${user.token}` },
          });
          if (res.data && (res.data.user || res.data.email || res.data._id)) {
            // getProfile returns profile directly, not wrapped in user object
            const profileData = res.data.user || res.data;
            setUserProfile(profileData);
          } else {
            // Fallback to Redux user data
            setUserProfile(user);
          }
        } catch (err) {
          // Fallback to Redux user data if API fails
          console.error("Failed to fetch user profile:", err);
          setUserProfile(user);
        } finally {
          setLoading(false);
        }
      } else {
        setLoading(false);
      }
    };
    fetchUserProfile();
  }, [user]);

  const formatDateOfBirth = () => {
    if (!userProfile || userProfile.bDay === undefined || userProfile.bMonth === undefined || userProfile.bYear === undefined) {
      return "Not set";
    }
    const day = userProfile.bDay;
    const month = monthNames[userProfile.bMonth - 1] || "Unknown";
    const year = userProfile.bYear;
    return `${day} ${month} ${year}`;
  };

  const handleDeleteEmail = async () => {
    if (!selectedEmail) return;

    setIsDeletingEmail(true);
    try {
      const res = await axios.delete(
        `${API_URL}/deleteEmail`,
        {
          data: { email: selectedEmail },
          headers: { Authorization: `Bearer ${user.token}` },
        }
      );

      if (res.data && res.data.user) {
        setUserProfile(res.data.user);
        setShowDeleteEmailModal(false);
        setShowEmailAccountModal(false);
        setSelectedEmail(null);
      }
    } catch (err) {
      alert(err.response?.data?.message || "Failed to delete email. Please try again.");
    } finally {
      setIsDeletingEmail(false);
    }
  };

  const isAdditionalEmail = () => {
    if (!selectedEmail || !userProfile) return false;
    return userProfile.additionalEmails && userProfile.additionalEmails.includes(selectedEmail);
  };

  const handleDeleteAccount = async () => {
    setIsDeletingAccount(true);
    try {
      // Validate token exists
      if (!user || !user.token) {
        throw new Error("Authentication token is missing. Please log in again.");
      }

      const res = await axios.delete(
        `${API_URL}/deleteAccount`,
        {
          headers: { 
            Authorization: `Bearer ${user.token}`,
            "Content-Type": "application/json"
          },
        }
      );

      if (res.data && res.data.success) {
        Cookies.remove("user");
        localStorage.removeItem("user");
        sessionStorage.clear();
        dispatch({ type: "LOGOUT" });
        setShowDeleteAccountModal(false);
        navigate("/login", { replace: true });
        return;
      }

      throw new Error(res.data?.message || "Failed to delete account.");
    } catch (err) {
      console.error("Failed to delete account:", err);
      const errorMessage = err.response?.data?.message || err.message || "Failed to delete account. Please try again.";
      alert(errorMessage);
      
      // If authentication failed, redirect to login
      if (err.response?.status === 400 && err.response?.data?.message?.includes("Authentification")) {
        Cookies.remove("user");
        localStorage.removeItem("user");
        dispatch({ type: "LOGOUT" });
        navigate("/login", { replace: true });
      }
    } finally {
      setIsDeletingAccount(false);
    }
  };

  return (
    <>
      {/* Go Back Button */}
      <button
        className="settings_back_btn"
        onClick={() => setVisible && setVisible(false)}
        aria-label="Go Back"
      >
        <XIcon />
        <span>Go Back</span>
      </button>

      <div className="settings_container personal_details_container">
        <div className="settings_header">
          <h2 className="settings_title">Personal details</h2>
          <p className="settings_subtitle">
            You decide what personal details you make visible to others.
          </p>
        </div>

        <div className="settings_content">
          {loading ? (
            <div className="personal_details_loading">Loading...</div>
          ) : (
              <div className="personal_details_sections">
              {/* Contact info Section */}
              <div className="personal_detail_section" style={{ cursor: "default" }}>
                <div className="personal_detail_content">
                  <h3 className="personal_detail_title">Contact info</h3>
                  {userProfile?.email ? (
                    <p className="personal_detail_value">{userProfile.email}</p>
                  ) : userProfile?.additionalEmails && userProfile.additionalEmails.length > 0 ? (
                    <p className="personal_detail_value">{userProfile.additionalEmails[0]}</p>
                  ) : (
                    <p className="personal_detail_value personal_detail_empty">Not set</p>
                  )}
                </div>
                {/* <div className="personal_detail_arrow">
                  <ChevronRightIcon />
                </div> */}
              </div>

              {/* Date of birth Section */}
              <div className="personal_detail_section" onClick={() => {
                // Navigate to date of birth edit page or show modal
                console.log("Date of birth clicked");
              }}>
                <div className="personal_detail_content">
                  <h3 className="personal_detail_title">Date of birth</h3>
                  <p className="personal_detail_value">{formatDateOfBirth()}</p>
                </div>
                {/* <div className="personal_detail_arrow">
                  <ChevronRightIcon />
                </div> */}
              </div>

              {/* Account ownership and control Section */}
              <div className="personal_detail_section" onClick={() => {
                // Navigate to account ownership and control page
                navigate("/settings/account-ownership", {
                  state: { from: "/settings" },
                });
              }}>
                <div className="personal_detail_content">
                  <h3 className="personal_detail_title">Account ownership and control</h3>
                  <p className="personal_detail_description">
                    Manage your data, modify your legacy contact, and deactivate or delete your accounts and profiles.
                  </p>
                </div>
                <div className="personal_detail_arrow">
                  <ChevronRightIcon />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Contact Information Modal */}
      {showContactModal && (
        <div className="contact_info_modal_overlay" onClick={() => setShowContactModal(false)}>
          <div className="contact_info_modal" onClick={(e) => e.stopPropagation()}>
            <div className="contact_info_modal_header">
              <h2 className="contact_info_modal_title">Contact information</h2>
              <button 
                className="contact_info_modal_close"
                onClick={() => setShowContactModal(false)}
                aria-label="Close"
              >
                <XIcon />
              </button>
            </div>
            
            <div className="contact_info_modal_content">
              <p className="contact_info_modal_description">
                Manage your  email addresses to make sure that your contact info is accurate and up to date.
              </p>
              
              <div className="contact_info_list">
                {/* Phone Number Entry */}
                {userProfile?.phoneNumber && (
                  <div 
                    className="contact_info_item" 
                    onClick={() => {
                      // Navigate to edit phone number
                      console.log("Phone number clicked");
                    }}
                    style={{ cursor: 'pointer' }}
                  >
                    <div className="contact_info_icon">
                      <PhoneIcon />
                    </div>
                    <div className="contact_info_text">
                      <span className="contact_info_value">{userProfile.phoneNumber}</span>
                    </div>
                    <div className="contact_info_arrow">
                      <ChevronRightIcon />
                    </div>
                  </div>
                )}
                
                {/* Primary Email Entry */}
                {userProfile?.email && (
                  <div 
                    className="contact_info_item" 
                    onClick={() => {
                      setSelectedEmail(userProfile.email);
                      setShowEmailAccountModal(true);
                    }}
                    style={{ cursor: 'pointer' }}
                  >
                    <div className="contact_info_icon">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                        <polyline points="22,6 12,13 2,6"/>
                      </svg>
                    </div>
                    <div className="contact_info_text">
                      <span className="contact_info_value">{userProfile.email}</span>
                    </div>
                    <div className="contact_info_arrow">
                      <ChevronRightIcon />
                    </div>
                  </div>
                )}

                {/* Additional Emails */}
                {userProfile?.additionalEmails && userProfile.additionalEmails.length > 0 && userProfile.additionalEmails.map((email, index) => (
                  <div 
                    key={index} 
                    className="contact_info_item" 
                    onClick={() => {
                      setSelectedEmail(email);
                      setShowEmailAccountModal(true);
                    }}
                    style={{ cursor: 'pointer' }}
                  >
                    <div className="contact_info_icon">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                        <polyline points="22,6 12,13 2,6"/>
                      </svg>
                    </div>
                    <div className="contact_info_text">
                      <span className="contact_info_value">{email}</span>
                    </div>
                    <div className="contact_info_arrow">
                      <ChevronRightIcon />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Email Account Information Modal */}
      {showEmailAccountModal && selectedEmail && (
        <div className="contact_info_modal_overlay" onClick={() => {
          setShowEmailAccountModal(false);
          setSelectedEmail(null);
        }}>
          <div className="contact_info_modal email_account_modal" onClick={(e) => e.stopPropagation()}>
            <div className="email_account_modal_header">
              <button 
                className="email_account_back_btn"
                onClick={() => {
                  setShowEmailAccountModal(false);
                  setSelectedEmail(null);
                }}
                aria-label="Go Back"
              >
                <ChevronLeftIcon />
              </button>
              <h2 className="email_account_modal_title">{selectedEmail}</h2>
              <button 
                className="contact_info_modal_close"
                onClick={() => {
                  setShowEmailAccountModal(false);
                  setSelectedEmail(null);
                }}
                aria-label="Close"
              >
                <XIcon />
              </button>
            </div>
            
            <div className="contact_info_modal_content">
              <p className="email_account_info_text">
                You added this email to these accounts.{" "}
                <span className="email_account_privacy_link"></span>
              </p>
              
              <div className="email_account_linked_section">
                <div className="linked_account_item linked_account_item_current">
                  <div className="linked_account_avatar">
                    <img 
                      src={userProfile.picture || user?.picture || '/default-avatar.png'} 
                      alt={userProfile.name || user?.name || 'Profile'}
                      onError={(e) => {
                        e.target.src = '/default-avatar.png';
                      }}
                    />
                  </div>
                  <div className="linked_account_info">
                    <div className="linked_account_name">
                      {userProfile.name || user?.name || 
                       (userProfile.first_name && userProfile.last_name 
                         ? `${userProfile.first_name} ${userProfile.last_name}` 
                         : userProfile.first_name || userProfile.last_name || 'User')}
                    </div>
                  </div>
                  <div className="contact_info_arrow">
                    <ChevronRightIcon />
                  </div>
                </div>
              </div>
              
              {isAdditionalEmail() && (
                <button 
                  className="email_account_delete_btn email_delete_btn"
                  onClick={() => {
                    setShowDeleteEmailModal(true);
                  }}
                  style={{ marginBottom: "12px" }}
                >
                  Delete email
                </button>
              )}
              
              <button 
                className="email_account_delete_btn"
                onClick={() => {
                  setShowDeleteAccountModal(true);
                }}
              >
                Delete account
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Email Confirmation Modal */}
      {showDeleteEmailModal && selectedEmail && (
        <div className="delete_account_modal_overlay" onClick={() => {
          if (!isDeletingEmail) {
            setShowDeleteEmailModal(false);
          }
        }}>
          <div className="delete_account_modal" onClick={(e) => e.stopPropagation()}>
            <div className="delete_account_modal_header">
              <h2 className="delete_account_modal_title">Delete email?</h2>
              <button 
                className="delete_account_modal_close"
                onClick={() => {
                  if (!isDeletingEmail) {
                    setShowDeleteEmailModal(false);
                  }
                }}
                aria-label="Close"
                disabled={isDeletingEmail}
              >
                <XIcon />
              </button>
            </div>
            
            <div className="delete_account_modal_content">
              <p className="delete_account_warning_text">
                Are you sure you want to remove <strong>{selectedEmail}</strong> from your account? You'll no longer be able to use this email address to log in or receive notifications.
              </p>
              
              <div className="delete_account_modal_actions">
                <button 
                  className="delete_account_cancel_btn"
                  onClick={() => {
                    if (!isDeletingEmail) {
                      setShowDeleteEmailModal(false);
                    }
                  }}
                  disabled={isDeletingEmail}
                >
                  Cancel
                </button>
                <button 
                  className="delete_account_confirm_btn"
                  onClick={handleDeleteEmail}
                  disabled={isDeletingEmail}
                  style={{ 
                    opacity: isDeletingEmail ? 0.6 : 1,
                    cursor: isDeletingEmail ? "not-allowed" : "pointer"
                  }}
                >
                  {isDeletingEmail ? "Deleting..." : "Delete"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Account Confirmation Modal */}
      {showDeleteAccountModal && (
        <div className="delete_account_modal_overlay" onClick={() => {
          if (!isDeletingAccount) {
            setShowDeleteAccountModal(false);
          }
        }}>
          <div className="delete_account_modal" onClick={(e) => e.stopPropagation()}>
            <div className="delete_account_modal_header">
              <h2 className="delete_account_modal_title">Delete account?</h2>
              <button 
                className="delete_account_modal_close"
                onClick={() => {
                  if (!isDeletingAccount) {
                    setShowDeleteAccountModal(false);
                  }
                }}
                aria-label="Close"
                disabled={isDeletingAccount}
              >
                <XIcon />
              </button>
            </div>
            
            <div className="delete_account_modal_content">
              <p className="delete_account_warning_text">
                You'll no longer be able to use this account to log in, reset your password or receive notifications. Please note that you may not be able to delete this account from accounts where it is the only contact method.
              </p>
              
              <div className="delete_account_modal_actions">
                <button 
                  className="delete_account_cancel_btn"
                  onClick={() => {
                    if (!isDeletingAccount) {
                      setShowDeleteAccountModal(false);
                    }
                  }}
                  disabled={isDeletingAccount}
                >
                  Cancel
                </button>
                <button 
                  className="delete_account_confirm_btn"
                  onClick={handleDeleteAccount}
                  disabled={isDeletingAccount}
                  style={{ 
                    opacity: isDeletingAccount ? 0.6 : 1,
                    cursor: isDeletingAccount ? "not-allowed" : "pointer"
                  }}
                >
                  {isDeletingAccount ? "Deleting..." : "Delete"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

